// ObjectKeysMenu.js
import { useState, useEffect, useRef } from "react"
import DialogWrapper from "../SwmmReact_Staging/DialogWrapper"
import TimeSeriesDialog from '../UI_TimeSeries/TimeSeriesDialog'
import ProfileDialog from '../UI_Profile/ProfileDialog'
import PatternsDialog from '../UI_Patterns/PatternsDialog'
import CurvesDialog from '../UI_Curves/CurvesDialog'
import PollutantsDialog from '../UI_Pollutants/PollutantsDialog'
import TransectsDialog from "../UI_Transects/TransectsDialog"
import AquifersDialog from '../UI_Aquifers/AquifersDialog'
import SnowPacksDialog from '../UI_SnowPacks/SnowPacksDialog'
import UnitHydrographsDialog from '../UI_UnitHydrographs/UnitHydrographsDialog'
import LIDControlDialog from '../UI_LIDControl/LIDControlDialog'
import LandUsesDialog from '../UI_LandUses/LandUsesDialog'
import { Button, Typography, Box } from '@mui/material'
import { renameNode, renameLink, renameSubcatchment, renameCurve, renameObject, renameLandUse, renamePollutant } from '../SwmmNode_Staging/EditModel'
import { Rnd } from "react-rnd"
import '../../css/DemoCode.css'
import '../../css/objectmodal.css'

/**
 * @param {JSON} model A state variable for SWMM model in JSON format.
 * @param {function} setModel state update function for updating 'model'.
 * @param {string} keysSection state variable for keys section value. Used for non-graphic elements.
 * @param {string} subkeysSection state variable for subkeys section value. Used for dual-key tables like 'CURVES' to identify the second key.
 * @param {function} setLayername state function for updating the layer that is begin operated on.
 * @param {function} setTargetID state function for setting the ID 'name' of the object being operated on
 * @param {function} setOpenOuter state function for identifying any menus or dialogs that are not children of this component.
 * @param {function} createFunction a function called when a create function is requested for an object type.
 * @param {function} deleteFunction a function called when a delete function is requested for an object type.
 * @param {function} handleCloseKeysMenu function called when the dialog is closed
 * @param {Object} keysDialogPosition state variable for the dialog location of a mapped object.
 * @param {function} setKeysDialogPosition state function for setting the dialog location of a mapped object.
 * @param {Object} uiDialogPosition state variable for the dialog location of an unmapped object.
 * @param {function} setUIDialogPosition state function for setting the dialog location of an unmapped object.
 * @param {function} handleZIndexAssign function for handling the updated z-index of a drag-and-drop dialog.
 * @returns UI for interactive swmm modeling.
 */
export default function ObjectKeysMenu({model, setModel, keysSection, subkeysSection='', setLayername, setTargetID, setOpenOuter, createFunction, deleteFunction, handleCloseKeysMenu, keysDialogPosition, setKeysDialogPosition, uiDialogPosition, setUIDialogPosition, handleZIndexAssign}) {
  const [submodel, setSubmodel] = useState({ [keysSection]: model[keysSection] })
  const [keys, setKeys]         = useState(Object.keys(model[keysSection]))
  const [selectedKey, setSelectedKey] = useState('')
  const [open, setOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [zIndex, setZIndex] = useState(3000)
  const keysRef = useRef(null)

  const handleClick = (ref) => {
    // Set the clicked div's z-index to the new max value
    setZIndex(handleZIndexAssign())
  }

  useEffect(()=>{
    if(subkeysSection === ''){
      setKeys(Object.keys(model[keysSection]))
  
      setSubmodel({
        [keysSection]: JSON.parse(JSON.stringify(model[keysSection]))
      })
    } else {
      model[keysSection][subkeysSection]?setKeys(Object.keys(model[keysSection][subkeysSection])):setKeys([])
  
      setSubmodel({
        [keysSection]: {
          ...model[keysSection],
          [subkeysSection]: model[keysSection][subkeysSection]?model[keysSection][subkeysSection]:{}
        }
      })
    }
  },[model])

  function showModal(layername, t1){
    async function stateChange() {
    }
    setOpenOuter(false)
    handleClose()
    setLayername(layername.toUpperCase())
    setTargetID(t1)
    setSelectedKey(t1)
    stateChange().then(()=>{
      setOpen(true)
      if(['JUNCTIONS', 'STORAGE', 'OUTFALLS', 'DIVIDERS', 'CONDUITS', 'PUMPS', 'ORIFICES', 'WEIRS', 'OUTLETS', 'SUBCATCHMENTS', 'RAINGAGES'].includes(layername))
        setOpenOuter(true)
    })
  }

  const handleClose = () => {
    setOpen(false)
    // These rename/update/delete functions should be part of swmmNode, not part of the UI.
    if(newName !== selectedKey && newName !== '' && ['JUNCTIONS', 'STORAGE', 'OUTFALLS', 'DIVIDERS'].includes(keysSection)){
      renameNode(model, setModel, keysSection, selectedKey, newName)
    } else 
    if(newName !== selectedKey && newName !== '' && ['CONDUITS', 'PUMPS', 'ORIFICES', 'WEIRS', 'OUTLETS'].includes(keysSection)){
      renameLink(model, setModel, keysSection, selectedKey, newName)
    } else 
    if(newName !== selectedKey && newName !== '' && ['SUBCATCHMENTS'].includes(keysSection)){
      renameSubcatchment(model, setModel, keysSection, selectedKey, newName)
    } else 
    if(newName !== selectedKey && newName !== '' && ['CURVES'].includes(keysSection)){
      renameCurve(model, setModel, keysSection, subkeysSection, selectedKey, newName)
    } else 
    if(newName !== selectedKey && newName !== '' && ['LANDUSES'].includes(keysSection)){
      renameLandUse(model, setModel, keysSection, selectedKey, newName)
    }
    if(newName !== selectedKey && newName !== '' && ['POLLUTANTS'].includes(keysSection)){
      renamePollutant(model, setModel, keysSection, selectedKey, newName)
    } else 
    if(newName !== selectedKey && newName !== ''){
      renameObject(model, setModel, keysSection, selectedKey, newName)
    }
    setNewName('')
  }
  
  const styleKeysMenu = {
    color: 'lightskyblue',
    top: '350px',
    left: '30%',
    bgcolor: 'background.paper',
    border: '5px solid rgba(200, 200, 200, 0.1)',
    borderRadius: '15px', 
    boxShadow: 24,
    p: 4,
  }

  let newObjectAttributes = {
    POLLUTANTS: {
      Units: "MG/L",
      Cppt: 0,
      Cgw: 0,
      Crdii: 0,
      Kdecay: 0,
      SnowOnly: "NO",
      CoPollutant: "*",
      CoFrac: 0,
      Cdwf: 0,
      Cinit: 0
    }, 
    LANDUSES: {
      Interval: "0",
      Available: "0",
      Cleaned: "0"
    },
    TIMESERIES: [
      {DateTime: 0, Value: 0}
    ],
    PROFILE: [
      {x: 0, y: 0}
    ],
    PATTERNS: {
      Type: 'MONTHLY', 
      Factors: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
    },
    AQUIFERS: {
      Por: 0.5,
      WP: 0.15,
      FC: 0.3,
      Ks: 5.0,
      Kslp: 10.0,
      Tslp: 15.0,
      ETu: 0.35,
      ETs: 14.0,
      Seep: 0.002,
      Ebot: 0.0,
      Egw: 10.0,
      Umc: 0.3,
      Epat: ''
    },
    SNOWPACKS: {
      PLOWABLE: {
        Cmin: 0.001,
        Cmax: 0.001,
        Tbase: 32,
        FWF: 0.1,
        SD0: 0.0,
        FW0: 0.0,
        SD100: 0.0
      },
      IMPERVIOUS: {
        Cmin: 0.001,
        Cmax: 0.001,
        Tbase: 32,
        FWF: 0.1,
        SD0: 0.0,
        FW0: 0.0,
        SD100: 0.0
      },
      PERVIOUS: {
        Cmin: 0.001,
        Cmax: 0.001,
        Tbase: 32,
        FWF: 0.1,
        SD0: 0.0,
        FW0: 0.0,
        SD100: 0.0
      },
      REMOVAL: {
        Dplow: 1.0,
        Fout: 0.0,
        Fimp: 0.0,
        Fperv: 0.0,
        Fimelt: 0.0,
        Fsub: 0.0,
        Scatch: ''
      }
    },
    HYDROGRAPHS: {
      Months: {
        All: {
          Long: {R:'', T:'', K:'', D0:'', Dmax:'', Drec:''},
          Medium: {R:'', T:'', K:'', D0:'', Dmax:'', Drec:''},
          Short: {R:'', T:'', K:'', D0:'', Dmax:'', Drec:''}
        }
      },
      Raingage: '',
    },
    TRANSECTS: {
      Nsta: 0,
      Xleft: 0,
      Xright: 0,
      Lfactor: 0,
      Wfactor: 0,
      Eoffset: 0,
      Nleft: 0.01,
      Nright: 0.01,
      Nchanl: 0.01,
      GR: [{
        Elev: 0,
        Station: 0
      }
      ]
    },
    LID_CONTROLS: {
      SURFACE: {
        StorHt: '1',
        VegFrac: '0.0',
        Rough: '0.1',
        Slope: '1.0',
        Xslope: '5'
      },
      SOIL: {
        Thick: '0',
        Por: '0.5',
        FC: '0.2',
        WP: '0.1',
        Ksat: '0.5',
        Kcoeff: '10.0',
        Suct: '3.5'
      },
      PAVEMENT: {
        Thick: '0',
        Vratio: '0.15',
        FracImp: '0',
        Perm: '100',
        Vclog: '0',
        regenInterval: '0',
        regenFrac: '0'
      },
      STORAGE: {
        Height: '0',
        Vratio: '0.75',
        Seepage: '0.5',
        Vclog: '0',
        Covered: 'NO'
      },
      DRAIN: {
        Coeff: '0',
        Expon: '0.5',
        Offset: '6',
        Delay: '0',
        Open: '0',
        Close: '0',
        Curve: ''
      },
      DRAINMAT: {
        Thick: '3',
        Vratio: '0.5',
        Rough: '0.1'
      },
      REMOVALS: {
        Pollutant: '',
        PctRemoval: ''
      },
      Type: 'VS',
    },
    CURVES: [
      {x: 0, y: 0}
    ]
  }

  function createNewObject(){
    // Get the next name:
    let newName = ''
    let usedNames = 
      subkeysSection===''?
      Object.keys(model[keysSection]):
      Object.keys(model[keysSection][subkeysSection]?model[keysSection][subkeysSection]:{})
    for(let i=1; i<100000 && newName === ''; i++){
      if(!usedNames.includes(i.toString())){
        newName = i.toString()
      }
    }

    let SECTION = {
      [newName]: newObjectAttributes[keysSection]
    }

    if(subkeysSection===''){
      setModel(prev=>({
        ...prev, 
        [keysSection]: {
          ...prev[keysSection], 
          ...SECTION
        }
      }))
    } else {
      setModel(prev=>({
        ...prev, 
        [keysSection]: {
          ...prev[keysSection], 
          [subkeysSection] : {
            ...prev[keysSection][subkeysSection],
            ...SECTION
          }
        }
      }))
    }

    //setOpen(true)
  }

  function deleteObject(id){
    
    if(subkeysSection===''){
      const SECTION = model[keysSection]
      delete SECTION[id]

      setModel(prev=>({
        ...prev, 
        [keysSection]: SECTION
      }))
    } else {
      const SECTION = model[keysSection][subkeysSection]
      delete SECTION[id]
      setModel(prev=>({
        ...prev, 
        [keysSection]: {
          ...prev[keysSection], 
          [subkeysSection] : {
            ...prev[keysSection][subkeysSection],
            ...SECTION
          }
        }
      }))
    }
  }

  function executeDelete(){
    if(deleteFunction){
      deleteFunction(selectedKey)
    } else {
      deleteObject(selectedKey)
    }
    handleClose()
  }

  function executeCreate(){
    if(createFunction){
      createFunction(selectedKey)
    } else{
      createNewObject()
    }
  }

  /**
   * If there is a valid model, create a UI for the model.
   */
  return(
    <>
    {
      <>
        <Rnd 
          ref={keysRef} 
          style={{ display: 'flex', zIndex: zIndex, flexDirection:'column'}} 
          cancel='Button, input, select' 
          onDragStop={(e, d) => { handleClick(keysRef); setKeysDialogPosition(prev =>({...prev, x: d.x, y: d.y })) }} 
          onResizeStop={(e, d, ref) => { handleClick(keysRef); setKeysDialogPosition(prev =>({...prev, width: ref.style.width, height: ref.style.height })) }}
          default={{ x: keysDialogPosition.x, y: keysDialogPosition.y, width: 320 }} 
          onClick={()=>{handleClick(keysRef)}} >
          <Box className="modal-objectmodal" sx={styleKeysMenu} style={{flex:1}} >
            <div style={{width: '100%'}}>
              <Button style={{height: '36.5px', width: '50px', right:'0', display:'flex', marginLeft:'auto'}} onClick={handleCloseKeysMenu}>Close</Button>
            </div>
            <div style={{width: '100%'}}>
              <div style={{flexGrow: '1'}}>
                <Typography id="modal-modal-title" variant="p4" >
                  <div style={{display:"flex", flexDirection: "column"}}>
                    <label>{keysSection}: {subkeysSection}</label>
                  </div>
                </Typography>
                <Button className="hover-button" style={{minWidth:0}} onClick={executeCreate}><span className="tooltip-text" id="top">{createFunction?`Create ${keysSection} object using the map`:`Add ${keysSection}`}</span>+</Button>
                
                <Button className="hover-button" style={{minWidth:0}} onClick={executeDelete}><span className="tooltip-text" id="top">{deleteFunction?`Click object on map to delete ${keysSection}`:`Delete selected ${keysSection}`}</span>-</Button>
              </div>
              <div style={{ flex:1, height:'calc(100% - 50px)', maxHeight: '800px', background:'black', overflowY: 'scroll'}}>
                {(keys.map(key => (
                  <Button fullWidth key={key} style={{textTransform: 'none', justifyContent: "flex-start"}} onClick={()=>{setOpen(false); showModal(keysSection, key)}}>{key}</Button>
                )))}
              </div>
            </div>
          </Box>
        </Rnd>
      </>
    }
      {
        selectedKey &&
        open &&
        <>
        {
          <DialogWrapper handleClose={handleClose} handleZIndexAssign={handleZIndexAssign} position={uiDialogPosition} setPosition={setUIDialogPosition} >
            {
              (keysSection==='TIMESERIES') &&
              <TimeSeriesDialog model={submodel} setModel={setSubmodel} targetType={'TIMESERIES'} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
            {
              (keysSection==='PROFILE') &&
              <ProfileDialog model={submodel} setModel={setSubmodel} targetType={'PROFILE'} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
            {
              (keysSection==='PATTERNS') &&
              <PatternsDialog model={submodel} setModel={setSubmodel} targetType={'PATTERNS'} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
            {
              (keysSection==='CURVES') &&
              <CurvesDialog model={submodel} setModel={setSubmodel} targetType={'CURVES'} subkeysSection={subkeysSection} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
            {
              (keysSection==='POLLUTANTS') &&
              <PollutantsDialog model={submodel} setModel={setSubmodel} targetType={'POLLUTANTS'} subkeysSection={subkeysSection} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
            {
              (keysSection==='TRANSECTS') &&
              <TransectsDialog model={submodel} setModel={setSubmodel} targetType={'TRANSECTS'} subkeysSection={subkeysSection} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
            {
              (keysSection==='LANDUSES') &&
              <LandUsesDialog model={submodel} setModel={setSubmodel} targetType={'LANDUSES'} subkeysSection={subkeysSection} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
            {
              (keysSection==='AQUIFERS') &&
              <AquifersDialog model={submodel} setModel={setSubmodel} targetType={'AQUIFERS'} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
            {
              (keysSection==='SNOWPACKS') &&
              <SnowPacksDialog model={submodel} setModel={setSubmodel} targetType={'SNOWPACKS'} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
            {
              (keysSection==='HYDROGRAPHS') &&
              <UnitHydrographsDialog model={submodel} setModel={setSubmodel} targetType={'HYDROGRAPHS'} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
            {
              (keysSection==='LID_CONTROLS') &&
              <LIDControlDialog model={submodel} setModel={setSubmodel} targetType={'LID_CONTROLS'} fullModel={model} setFullModel={setModel} target={selectedKey} setNewName={setNewName} />
            }
          </DialogWrapper>
        }
        </>
      }
    </>
  )
} 